import React, { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './layouts/Layout';
import Layout3D from './layouts/Layout3D';
import { BalanceProvider } from './contexts/BalanceContext';
import { PlayerProvider, usePlayerProgress } from './contexts/PlayerContext';
import { LiveBetsProvider } from './contexts/LiveBetsContext';
import { ChatProvider } from './contexts/ChatContext';

import { BuffsProvider } from './contexts/BuffsContext';
import AchievementToasts from './components/AchievementToasts';
import { useConnection } from './hooks/useConnection';
import { PATH_TO_UNLOCK, UNLOCK_LEVEL_MAP } from './constants/levelUnlocks';
import './styles/index.css';

// Detect if running inside s&box WebPanel
const isSbox = typeof window !== 'undefined' &&
  (new URLSearchParams(window.location.search).has('panelTicket') ||
    new URLSearchParams(window.location.search).has('token'));

// Fix inverted scroll in s&box panels
if (isSbox) {
  window.addEventListener('wheel', (e) => {
    e.preventDefault();
    const target = e.target.closest('[data-scroll]') ||
      e.target.closest('.casino-3d-content') ||
      e.target.closest('.dice-page') ||
      e.target.closest('[style*="overflow"]') ||
      document.querySelector('.casino-3d-content');
    if (target) {
      target.scrollTop += e.deltaY * -1;
    }
  }, { passive: false, capture: true });
}

// Lazy load games — hoisted outside component, never recreated
const Homepage = lazy(() => import('./games/Homepage'));
const Tower = lazy(() => import('./games/Tower'));
const Mines = lazy(() => import('./games/Mines'));
const Dice = lazy(() => import('./games/Dice'));
const ChickenCross = lazy(() => import('./games/ChickenCross'));
const Wheel = lazy(() => import('./games/Wheel'));
const Keno = lazy(() => import('./games/Keno'));
const Limbo = lazy(() => import('./games/Limbo'));
const Plinko = lazy(() => import('./games/Plinko'));
const Crash = lazy(() => import('./games/Crash'));
const CrashDisplay = lazy(() => import('./games/CrashDisplay'));
const LeaderboardDisplay = lazy(() => import('./games/LeaderboardDisplay'));
const SlotDisplay = lazy(() => import('./games/SlotDisplay'));
const Achievements = lazy(() => import('./games/Achievements'));
const HiLo = lazy(() => import('./games/HiLo'));
const GameSelect = lazy(() => import('./games/GameSelect'));
const InitialLoadContext = React.createContext(false);

class GameErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, color: '#e04848', background: '#0e0d0a', minHeight: '100vh', fontFamily: 'monospace' }}>
          <h2 style={{ color: '#f87171' }}>{i18n.t('common:game_crashed')}</h2>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#fca5a5', fontSize: 14 }}>
            {this.state.error?.message || i18n.t('common:unknown_error')}
          </pre>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#94a3b8', fontSize: 12, marginTop: 16 }}>
            {this.state.error?.stack || ''}
          </pre>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.hash = '#/'; }}
            style={{ marginTop: 20, padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
          >
            {i18n.t('common:go_home')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const GameLoader = () => {
  const loadingScreenHidden = React.useContext(InitialLoadContext);
  const { t } = useTranslation('common');
  if (!loadingScreenHidden) return null;

  return (
    <div className="game-loader">
      <div className="game-loader-spinner-container">
        <div className="game-loader-spinner-outer"></div>
        <div className="game-loader-spinner"></div>
        <div className="game-loader-spinner-dot"></div>
      </div>
      <span className="game-loader-text">{t('loading')}</span>
    </div>
  );
};

const ConnectionError = ({ message }) => {
  const { t } = useTranslation('common');
  if (window.hideLoadingScreen) window.hideLoadingScreen();
  return (
    <div className="spinbox-loader">
      <div className="spinbox-loader__pattern" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/casino_pattern.png)` }}></div>
      <div className="spinbox-loader__vignette"></div>
      <div className="spinbox-loader__content spinbox-loader__content--error">
        <div className="spinbox-loader__brand">
          <div className="spinbox-loader__stars">★  ★  ★</div>
          <div className="spinbox-loader__title">S&amp;SINO</div>
          <div className="spinbox-loader__subtitle">CASINO</div>
        </div>
        <div className="spinbox-loader__divider"></div>
        <div className="spinbox-loader__error-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <h2 className="spinbox-loader__error-title">{t('connection_failed')}</h2>
        <p className="spinbox-loader__error-message">{message}</p>
        <button className="spinbox-loader__retry-btn" onClick={() => window.location.reload()}>
          <span>{t('retry_connection')}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

// Game guard — redirects to home if game is locked
const GameGuard = ({ children, path }) => {
  const { unlockedItems, level } = usePlayerProgress();
  const unlockId = PATH_TO_UNLOCK[path];
  const requiredLevel = unlockId ? UNLOCK_LEVEL_MAP[unlockId] : null;
  const isUnlocked = !unlockId || unlockedItems.includes(unlockId) || (requiredLevel != null && level >= requiredLevel);
  if (!isUnlocked) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Game routes shared between both layouts
const gameRoutes = (
  <>
    <Route path="/tower" element={<GameGuard path="/tower"><Tower /></GameGuard>} />
    <Route path="/dice" element={<GameGuard path="/dice"><Dice /></GameGuard>} />
    <Route path="/mines" element={<GameGuard path="/mines"><Mines /></GameGuard>} />
    <Route path="/chicken" element={<GameGuard path="/chicken"><ChickenCross /></GameGuard>} />
    <Route path="/wheel" element={<GameGuard path="/wheel"><Wheel /></GameGuard>} />
    <Route path="/limbo" element={<GameGuard path="/limbo"><Limbo /></GameGuard>} />
    <Route path="/plinko" element={<GameGuard path="/plinko"><Plinko /></GameGuard>} />
    <Route path="/crash" element={<GameGuard path="/crash"><Crash /></GameGuard>} />
    <Route path="/keno" element={<GameGuard path="/keno"><Keno /></GameGuard>} />
    <Route path="/hilo" element={<GameGuard path="/hilo"><HiLo /></GameGuard>} />
  </>
);

// Full web routes (original S&Sino experience)
const webRoutes = (
  <Routes>
    <Route path="/" element={<Homepage />} />
    {gameRoutes}
    <Route path="/achievements" element={<Achievements />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

// 3D s&box routes (minimal: game select + games only)
const sboxRoutes = (
  <Routes>
    <Route path="/" element={<GameSelect />} />
    {gameRoutes}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

// Routes rendered without any layout wrapper (fullscreen display views)
const layoutFreeRoutes = (
  <Routes>
    <Route path="/crash-display" element={<CrashDisplay />} />
    <Route path="/leaderboard-display" element={<LeaderboardDisplay />} />
    <Route path="/slot" element={<SlotDisplay />} />
    <Route path="/slot-display" element={<SlotDisplay />} />
    <Route path="/slot-idle" element={<SlotDisplay />} />
  </Routes>
);

// Paths that should bypass the layout
const layoutFreePaths = ['/crash-display', '/leaderboard-display', '/slot', '/slot-display', '/slot-idle'];

function RoutedApp({ isReady }) {
  const location = useLocation();
  const isLayoutFree = layoutFreePaths.some(p => location.pathname.startsWith(p));

  // Display routes render immediately — they manage their own connection
  if (isLayoutFree) {
    return (
      <GameErrorBoundary>
        <Suspense fallback={<GameLoader />}>
          {layoutFreeRoutes}
        </Suspense>
      </GameErrorBoundary>
    );
  }

  // Normal routes wait for auth
  if (!isReady) return null;

  const ActiveLayout = isSbox ? Layout3D : Layout;
  const activeRoutes = isSbox ? sboxRoutes : webRoutes;

  return (
    <ActiveLayout>
      <GameErrorBoundary>
        <Suspense fallback={<GameLoader />}>
          {activeRoutes}
        </Suspense>
      </GameErrorBoundary>
      {!isSbox && <AchievementToasts />}
    </ActiveLayout>
  );
}

function AppContent() {
  const conn = useConnection();

  if (conn.connectionError) return <ConnectionError message={conn.connectionError} />;

  const isReady = conn.isConnected && conn.isInitialized;

  return (
    <ChatProvider>
    <BuffsProvider>
    <LiveBetsProvider>
    <InitialLoadContext.Provider value={conn.loadingScreenHidden}>
    <HashRouter>
      <RoutedApp isReady={isReady} />
    </HashRouter>
    </InitialLoadContext.Provider>
    </LiveBetsProvider>
    </BuffsProvider>
    </ChatProvider>
  );
}

export default function App() {
  // Display routes are fully self-contained — no auth, no providers, no useConnection.
  // Check the hash before mounting any providers so the big screen never triggers auth.
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const isDisplayRoute = layoutFreePaths.some(p => hash.includes(p));

  if (isDisplayRoute) {
    return (
      <HashRouter>
        <GameErrorBoundary>
          <Suspense fallback={null}>
            {layoutFreeRoutes}
          </Suspense>
        </GameErrorBoundary>
      </HashRouter>
    );
  }

  return (
    <BalanceProvider>
      <PlayerProvider>
        <AppContent />
      </PlayerProvider>
    </BalanceProvider>
  );
}
